import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ApiService from '../../services/Api.service';
import { useDispatch } from '../../store/hooks';
import { setSelectedTrack } from '../../store/slices/track.slice';
import { iTrack } from '../../types/ITrack';
import { formatMusicalKey } from '../../utils/formatMusicalKey';
import CamelotKeyChip from '../tracks/CamelotKeyChip';
import { TrackRowViewTrack } from '../tracks/TrackRowView';
import { ParsedTrackRow } from './AgentChatTables';
import {
  artworkKey,
  fetchLibraryTracksByIds,
  isCatalogTrackId,
  isLocalTrackId,
  librarySummaryToTrack,
} from './AgentChatTrackHydrate';

const matchColors: Record<string, string> = {
  'Perfect match': '#2fdf75',
  'Energy boost': '#d3771c',
  'Energy drop': '#50c8ff',
  'Mood change': '#ee7bb4',
  Compatible: 'rgba(255,255,255,0.5)',
};

const catalogCache = new Map<string, iTrack>();

const asView = (
  row: ParsedTrackRow,
  extra?: iTrack | null,
): TrackRowViewTrack => {
  const keyFromExtra =
    extra?.Key_Camelot ||
    (typeof extra?.Key === 'string' ? extra.Key : undefined);

  return {
    id: extra?.ID || row.id,
    artist: extra?.Artist || row.artist,
    title: extra?.Title || row.title,
    albumCover: extra?.AlbumCover || undefined,
    bpm: extra?.BPM ?? row.bpm,
    key: formatMusicalKey(keyFromExtra || row.key) || undefined,
    match: row.match,
    energy: extra?.energy ?? row.energy,
    danceability: extra?.danceability ?? row.danceability,
    valence: extra?.valence ?? row.valence,
    trackNum: row.trackNum,
  };
};

const asTrack = (view: TrackRowViewTrack): iTrack => ({
  ID: view.id || '',
  Artist: view.artist || '',
  Title: view.title || '',
  AlbumCover: view.albumCover,
  BPM: typeof view.bpm === 'number' ? view.bpm : Number(view.bpm) || undefined,
  Key_Camelot: view.key,
  Key: view.key,
  energy: view.energy,
  danceability: view.danceability,
  valence: view.valence,
});

const mergeTrack = (
  prev: Record<string, iTrack>,
  id: string,
  patch: iTrack,
  preferNewCover: boolean,
): Record<string, iTrack> => {
  const current = prev[id];
  const albumCover = preferNewCover
    ? patch.AlbumCover || current?.AlbumCover
    : current?.AlbumCover || patch.AlbumCover;
  return {
    ...prev,
    [id]: {
      ...current,
      ...patch,
      ID: patch.ID || current?.ID || id,
      Artist: patch.Artist || current?.Artist || '',
      Title: patch.Title || current?.Title || '',
      AlbumCover: albumCover,
    },
  };
};

export const AgentChatTrackList: React.FC<{ rows: ParsedTrackRow[] }> = ({
  rows,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [hydrated, setHydrated] = useState<Record<string, iTrack>>({});

  const idKey = rows.map((row) => row.id ?? '').join('|');
  const nameKey = rows.map((row) => `${row.artist}:${row.title}`).join('|');

  useEffect(() => {
    let cancelled = false;
    const ids = idKey.split('|').filter(Boolean);

    setHydrated((prev) => {
      let next = prev;
      for (const row of rows) {
        const id = row.id || artworkKey(row.artist, row.title);
        const cachedTrack = row.id ? catalogCache.get(row.id) : undefined;
        if (!cachedTrack) continue;
        next = mergeTrack(
          next,
          id,
          {
            ...cachedTrack,
            ID: cachedTrack.ID || row.id || '',
            Artist: cachedTrack.Artist || row.artist,
            Title: cachedTrack.Title || row.title,
          },
          true,
        );
      }
      return next;
    });

    const apply = (id: string, patch: iTrack, preferNewCover: boolean) => {
      if (cancelled) return;
      setHydrated((prev) => mergeTrack(prev, id, patch, preferNewCover));
    };

    void (async () => {
      const catalogIds = ids.filter(isCatalogTrackId);
      const localIds = ids.filter(isLocalTrackId).map(Number);
      const missingCatalog = catalogIds.filter((id) => !catalogCache.has(id));

      const catalogWork = Promise.all(
        missingCatalog.map(async (id) => {
          const track = await ApiService.getTrackById(id);
          if (track?.ID) {
            catalogCache.set(id, track);
            catalogCache.set(track.ID, track);
            apply(id, track, true);
          }
        }),
      );

      for (const id of catalogIds) {
        const track = catalogCache.get(id);
        if (track) apply(id, track, true);
      }

      const localWork = fetchLibraryTracksByIds(localIds).then((summaries) => {
        for (const summary of summaries) {
          const track = librarySummaryToTrack(summary);
          catalogCache.set(String(summary.id), track);
          apply(String(summary.id), track, true);
        }
      });

      await Promise.all([catalogWork, localWork]);
    })();

    return () => {
      cancelled = true;
    };
  }, [idKey, nameKey]);

  return (
    <div className="agent-track-list">
      {rows.map((row, index) => {
        const extra =
          (row.id && hydrated[row.id]) ||
          hydrated[artworkKey(row.artist, row.title)] ||
          undefined;
        const view = asView(row, extra);
        const clickable = Boolean(view.id);
        const bpm =
          typeof view.bpm === 'number' && Number.isFinite(view.bpm)
            ? Math.round(view.bpm)
            : null;
        const matchColor = view.match ? matchColors[view.match] : undefined;
        return (
          <button
            key={`${view.id || view.title}-${index}`}
            type="button"
            className={`agent-track-row${view.match ? ' has-match' : ''}`}
            disabled={!clickable}
            onClick={
              clickable
                ? () => {
                    dispatch(setSelectedTrack(asTrack(view)));
                    history.push('/track');
                  }
                : undefined
            }
          >
            {view.albumCover ? (
              <img src={view.albumCover} alt="" />
            ) : (
              <span className="agent-track-art-fallback" />
            )}
            <span className="agent-track-copy">
              <span className="agent-track-title">
                {view.title || 'Untitled'}
              </span>
              <span className="agent-track-artist">{view.artist || '—'}</span>
            </span>
            <span className="agent-track-meta">
              <span className="agent-track-keyline">
                <CamelotKeyChip value={view.key} />
                <span className="agent-track-bpm">
                  {bpm != null ? (
                    <>
                      {bpm}
                      <span className="bpm-unit"> bpm</span>
                    </>
                  ) : (
                    '—'
                  )}
                </span>
              </span>
              {view.match && (
                <span
                  className="agent-track-match"
                  style={matchColor ? { color: matchColor } : undefined}
                >
                  {view.match}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default AgentChatTrackList;
